import { GlobalOutlined } from '@ant-design/icons';
import { Divider as AntDivider, Select as AntSelect, Space, Typography } from 'antd';
import React from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { jt, t } from 'ttag';

import ColourLogo from '../../../assets/EM-logo-colour-dark.svg';
import { EVENTMOBI_URLS } from '../../../constants/ExternalUrls';
import useTranslation from '../../../hooks/useTranslation';
import { ISO_LOCALE_MAP, LANGUAGE_BY_ISO } from '../../../services/translation/api';
import { isFFEnabled } from '../../../state/flags';
import FeatureFlags from '../../../types/FeatureFlags';
import CenteredLayout from '../Layout';

import MarketingPanel from './MarketingPanel';

const { Link, Text } = Typography;

// TODO: confirm the real EventMobi sales/demo marketing URLs.
const SALES_URL = 'https://eventmobi.com/contact-us/';
const DEMO_URL = 'https://eventmobi.com/demo/';

const Container = styled.div`
  display: flex;
  min-height: 100vh;

  /* keep panel padding inside the 50/50 split */
  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }
`;

const Marketing = styled.div`
  display: flex;
  flex: 1 1 50%;
`;

const FormSide = styled.div`
  position: relative;
  display: flex;
  flex: 1 1 50%;
  flex-direction: column;
  padding: 80px;
  background: #fff;

  /* the shared auth form centers its title; left-align it for this layout */
  .ant-form h4.ant-typography {
    align-self: flex-start;
    text-align: left;
  }
`;

const LanguagePicker = styled.div`
  position: absolute;
  top: 24px;
  right: 24px;
  display: flex;
  align-items: center;
  gap: 8px;
  border-bottom: 1px solid #d9d9d9;
`;

const Center = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const Inner = styled.div`
  width: 100%;
`;

const LogoLink = styled.a`
  display: inline-block;
  margin-bottom: 24px;
`;

const Logo = styled.img`
  display: block;
  width: 150px;
`;

const Footer = styled.div`
  display: flex;
  justify-content: center;
`;

interface Props {
  children: React.ReactNode;
  showSalesPrompt?: boolean;
}

const SplitAuthLayout = ({ children, showSalesPrompt = false }: Props) => {
  const { language, setLanguage } = useTranslation();

  const salesLink = (
    <Link key="sales" href={SALES_URL} rel="nofollow noopener noreferrer" target="_blank">
      {t`Sales Team`}
    </Link>
  );
  const demoLink = (
    <Link key="demo" href={DEMO_URL} rel="nofollow noopener noreferrer" target="_blank">
      {t`Book a Demo`}
    </Link>
  );

  return (
    <Container>
      <FormSide>
        <LanguagePicker>
          <GlobalOutlined style={{ fontSize: 18, color: '#8c8c8c' }} />
          <AntSelect
            variant="borderless"
            popupMatchSelectWidth={false}
            placeholder={t`Language`}
            value={language}
            onChange={(lang: string) => setLanguage(lang)}
            options={Object.keys(ISO_LOCALE_MAP).map(code => ({
              value: code,
              label: LANGUAGE_BY_ISO(code),
            }))}
          />
        </LanguagePicker>
        <Center>
          <Inner>
            <LogoLink href={EVENTMOBI_URLS.HOME_WITH_UTM} rel="noopener noreferrer" target="_blank">
              <Logo src={ColourLogo} alt="EventMobi" />
            </LogoLink>
            {children}
            {showSalesPrompt && (
              <div style={{ marginTop: 24 }}>
                <Text>{jt`Don't have an account? Talk to our ${salesLink} or ${demoLink}!`}</Text>
              </div>
            )}
          </Inner>
        </Center>
        <Footer>
          <Space separator={<AntDivider orientation="vertical" />}>
            <Link
              href={EVENTMOBI_URLS.TERMS_OF_USE}
              rel="nofollow noopener noreferrer"
              target="_blank"
            >{t`Terms of Use`}</Link>
            <Link
              href={EVENTMOBI_URLS.PRIVACY_POLICY}
              rel="nofollow noopener noreferrer"
              target="_blank"
            >{t`Privacy Policy`}</Link>
          </Space>
        </Footer>
      </FormSide>
      <Marketing>
        <MarketingPanel />
      </Marketing>
    </Container>
  );
};

const LoginLayout = (props: Omit<Props, 'showSalesPrompt'>) => (
  <SplitAuthLayout showSalesPrompt {...props} />
);

export const GatedLoginLayout = ({ children }: { children: React.ReactNode }) => {
  const isRedesignEnabled = useSelector(state => isFFEnabled(state, FeatureFlags.LoginRedesign));
  const Layout = isRedesignEnabled ? LoginLayout : CenteredLayout;
  return <Layout>{children}</Layout>;
};

export const GatedAuthLayout = ({ children }: { children: React.ReactNode }) => {
  const isRedesignEnabled = useSelector(state => isFFEnabled(state, FeatureFlags.LoginRedesign));
  const Layout = isRedesignEnabled ? SplitAuthLayout : CenteredLayout;
  return <Layout>{children}</Layout>;
};
